/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Barre de navigation du site UK.
 *
 * Elle est en `fixed` et se pose par-dessus le contenu : les pages doivent donc
 * reserver sa hauteur (`pt-16 md:pt-20`), sauf l'accueil dont le hero passe
 * volontairement dessous.
 *
 * Le fond reste clair et opaque des le haut de page. Il a ete translucide a
 * 60 % : par-dessus le hero sombre, le blanc virait au gris et les libelles
 * ardoise devenaient illisibles tant qu'on n'avait pas commence a defiler.
 */
import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Flag from "./Flag";
import Wordmark, { lockupHeightEm } from "./Wordmark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Menu, X, ChevronDown } from "lucide-react";

const Navbar = () => {
  const { t, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (!t) return null;

  const nav = t.navigation;
  const services = t.services?.items || [];

  const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? "text-blue-600" : "text-slate-700 hover:text-slate-950"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white/90 backdrop-blur-xl border-b border-transparent"
      }`}
    >
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="group flex items-center gap-2.5 text-[2.05rem] sm:text-[2.5rem] lg:text-[3rem]"
          >
            <img
              src="/images/logo-prenim-uk.webp"
              alt=""
              aria-hidden="true"
              width="40"
              height="45"
              style={{ height: `${lockupHeightEm(0.22)}em` }}
              className="w-auto shrink-0 object-contain"
            />
            {/* La taille posee ici pilote tout le verrou : le nom vaut 1em et la
                signature 0.22em, si bien que le bloc reste homothetique d'un
                point d'arret a l'autre. */}
            <Wordmark
              name="Prenim"
              tagline="Digital Transformation"
              ratio={0.22}
              testId="navbar-wordmark"
              nameClassName="text-slate-950 transition-colors group-hover:text-blue-600"
              taglineClassName="text-slate-500 transition-colors group-hover:text-blue-600"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <NavLink to="/" data-testid="nav-link-home" className={navLinkClass} end>
              {nav.home}
            </NavLink>
            <NavLink to="/about" data-testid="nav-link-about" className={navLinkClass}>
              {nav.about}
            </NavLink>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-services-trigger"
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950 outline-none"
              >
                {nav.services}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[280px]">
                <DropdownMenuItem asChild>
                  <Link
                    to="/services"
                    data-testid="nav-services-overview"
                    className="cursor-pointer font-medium"
                  >
                    {t.common.all_services}
                  </Link>
                </DropdownMenuItem>
                {services.map((s) => (
                  <DropdownMenuItem key={s.slug} asChild>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`nav-services-${s.slug}`}
                      className="cursor-pointer"
                    >
                      {s.title}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/projects" data-testid="nav-link-projects" className={navLinkClass}>
              {nav.projects}
            </NavLink>
            <NavLink to="/careers" data-testid="nav-link-careers" className={navLinkClass}>
              {nav.careers}
            </NavLink>
            <NavLink to="/contact" data-testid="nav-link-contact" className={navLinkClass}>
              {nav.contact}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <NavLink
              to="/tchad"
              data-testid="nav-link-tchad"
              className={({ isActive }) =>
                `hidden lg:inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border-2 transition-colors ${
                  isActive
                    ? "bg-slate-950 text-white border-slate-950"
                    : "border-slate-300 text-slate-700 hover:border-slate-950 hover:text-slate-950"
                }`
              }
            >
              <Flag code="td" className="me-1.5 align-[-2px]" />
              Prenim Tchad
            </NavLink>
            <LanguageSwitcher />
            <Link
              to="/contact"
              data-testid="navbar-cta"
              className="hidden md:inline-flex items-center rounded-full bg-slate-950 text-white text-sm font-semibold px-4 py-2 hover:bg-blue-600 transition-colors"
            >
              {nav.cta}
            </Link>
            <button
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen((p) => !p)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-200 text-slate-800"
              aria-label="menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-4 py-4 space-y-1">
            {[
              { to: "/", label: nav.home, key: "home" },
              { to: "/about", label: nav.about, key: "about" },
              { to: "/services", label: nav.services, key: "services" },
              { to: "/projects", label: nav.projects, key: "projects" },
              { to: "/careers", label: nav.careers, key: "careers" },
              { to: "/contact", label: nav.contact, key: "contact" },
            ].map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`mobile-link-${item.key}`}
                className="block px-3 py-2 rounded-md text-slate-800 hover:bg-slate-100 font-medium"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/tchad"
              data-testid="mobile-link-tchad"
              className="block px-3 py-2 rounded-md text-slate-800 hover:bg-slate-100 font-medium"
            >
              <Flag code="td" className="me-1.5 align-[-2px]" />
              Prenim Tchad
            </Link>
            <Link
              to="/contact"
              data-testid="mobile-cta"
              className="mt-2 block text-center rounded-full bg-slate-950 text-white font-semibold px-4 py-2.5"
            >
              {nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
